/**
 * Dashboard del store — réplica visual 1:1 del backoffice de Skeleton:
 *
 *   Row 1 (header): "Hola, <admin>" + fecha completa + selector período + "Comparar fechas"
 *   Row 2: 4 KPI cards (Rendimiento de Ventas / Total Ventas / Ingreso Promedio / Valor Promedio Orden)
 *   Row 3: Ingresos (Pagadas) [gráfica área] + Productos Populares [lista con barras]
 *   Row 4: Ventas (Pagadas) [gráfica barras] + Sesiones [gráfica barras]
 *
 * Fuente de datos: mientras el módulo de orders + analytics aún no esté en core-api,
 * usamos los KPIs reales que sí están disponibles (productos count desde admin stats)
 * y mock data con shape correcto para las gráficas, marcado claramente con "demo".
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { Card, CardContent } from '@/components/ui'
import { adminApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  TrendingUp, TrendingDown, MoreVertical, Calendar, Info,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/')({
  component: StoreDashboard,
})

type Period = 'day' | 'week' | 'month' | 'year'

interface AnalyticsResponse {
  period: Period
  from: string
  to: string
  kpis: {
    totalRevenue: number
    totalRevenueDeltaPct: number
    totalOrders: number
    totalOrdersDeltaPct: number
    avgOrderValue: number
    avgOrderValueDeltaPct: number
    avgIncome: number
    avgIncomeDeltaPct: number
  }
  revenueChart: Array<{ name: string; actual: number; previous: number }>
  ordersChart: Array<{ name: string; actual: number; previous: number }>
  sessionsChart: Array<{ name: string; value: number }>
  popularProducts: Array<{ id: string; name: string; sales: number }>
}

function StoreDashboard() {
  const { id } = Route.useParams()
  const [period, setPeriod] = useState<Period>('week')

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['tenant-analytics', id, period],
    queryFn: () => adminApi.get<{ data: AnalyticsResponse }>(`/api/admin/tenants/${id}/analytics?period=${period}`),
  })

  const a = analytics?.data
  const revenueChart = a?.revenueChart ?? []
  const ordersChart = a?.ordersChart ?? []
  const sessionsChart = a?.sessionsChart ?? []
  const popularProducts = a?.popularProducts ?? []
  const k = a?.kpis ?? {
    totalRevenue: 0, totalRevenueDeltaPct: 0,
    totalOrders: 0, totalOrdersDeltaPct: 0,
    avgOrderValue: 0, avgOrderValueDeltaPct: 0,
    avgIncome: 0, avgIncomeDeltaPct: 0,
  }
  const totalSessions = sessionsChart.reduce((s, d) => s + d.value, 0)

  const today = new Date()
  const dateStr = today.toLocaleDateString('es-CO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })
  const adminName = 'Administrador'

  return (
    <div className="p-8 space-y-6">
      {/* Header — saludo + fecha + selector período + comparar */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Hola, {adminName}</h1>
          <p className="text-sm text-neutral-500 mt-1 capitalize">{dateStr}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-neutral-700 hover:border-neutral-600 transition" title="Próxima iteración">
            <Calendar className="size-3.5" /> Comparar fechas
          </button>
          <div className="flex items-center bg-neutral-900 border border-neutral-800 rounded-md p-0.5 text-xs">
            {(['day', 'week', 'month', 'year'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={cn(
                  'px-3 py-1 rounded transition',
                  period === p ? 'bg-neutral-700 text-white' : 'text-neutral-400 hover:text-white',
                )}
              >
                {p === 'day' ? 'Día' : p === 'week' ? 'Semana' : p === 'month' ? 'Mes' : 'Año'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Row 1: 4 KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard label="Rendimiento de Ventas" value={formatCop(k.totalRevenue)} change={k.totalRevenueDeltaPct} compareLabel="vs período anterior" />
        <KpiCard label="Total Ventas" value={String(k.totalOrders)} change={k.totalOrdersDeltaPct} compareLabel="vs período anterior" />
        <KpiCard label="Ingreso Promedio" value={formatCop(k.avgIncome)} change={k.avgIncomeDeltaPct} compareLabel="vs período anterior" />
        <KpiCard label="Valor Promedio de Orden" value={formatCop(k.avgOrderValue)} change={k.avgOrderValueDeltaPct} compareLabel="vs período anterior" />
      </div>

      {/* Row 2: Ingresos + Productos Populares */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RevenueChartCard data={revenueChart} total={k.totalRevenue} deltaPct={k.totalRevenueDeltaPct} />
        <PopularProductsCard products={popularProducts} loading={isLoading} />
      </div>

      {/* Row 3: Ventas + Sesiones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <OrdersChartCard data={ordersChart} total={k.totalOrders} deltaPct={k.totalOrdersDeltaPct} />
        <SessionsChartCard data={sessionsChart} total={totalSessions} />
      </div>
    </div>
  )
}

/* ============================================================
 * KPI Card
 * ============================================================ */

function KpiCard({ label, value, change, compareLabel }: {
  label: string
  value: string
  change: number
  compareLabel: string
}) {
  const isUp = change >= 0
  const Icon = isUp ? TrendingUp : TrendingDown
  const color = change === 0 ? 'text-neutral-500' : isUp ? 'text-emerald-400' : 'text-red-400'

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <p className="text-sm text-neutral-400">{label}</p>
          <button className="text-neutral-500 hover:text-neutral-300">
            <MoreVertical className="size-4" />
          </button>
        </div>
        <p className="text-3xl font-bold tabular-nums">{value}</p>
        <div className={cn('flex items-center gap-1 mt-2 text-xs', color)}>
          <Icon className="size-3" />
          <span className="font-medium">{change >= 0 ? '↑' : '↓'}{Math.abs(change)}%</span>
          <span className="text-neutral-500">{compareLabel}</span>
        </div>
      </CardContent>
    </Card>
  )
}

/* ============================================================
 * Revenue (Ingresos Pagadas) — Area chart
 * ============================================================ */

function RevenueChartCard({ data, total, deltaPct }: { data: Array<{ name: string; actual: number; previous: number }>; total: number; deltaPct: number }) {
  const isUp = deltaPct >= 0
  const Icon = isUp ? TrendingUp : TrendingDown
  const color = deltaPct === 0 ? 'text-neutral-500' : isUp ? 'text-emerald-400' : 'text-red-400'

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-1.5">
            Ingresos (Pagadas)
            <Info className="size-3.5 text-neutral-500" />
          </h3>
        </div>
        <p className="text-3xl font-bold tabular-nums">{formatCop(total)}</p>
        <div className="flex items-center gap-2 mt-1 text-xs">
          <span className={cn('flex items-center gap-1', color)}>
            <Icon className="size-3" /> {deltaPct >= 0 ? '↑' : '↓'}{Math.abs(deltaPct)}% vs período anterior
          </span>
        </div>
        <p className="text-xs text-neutral-500 mt-1">Últimos 7 días</p>

        <div className="flex items-center gap-3 mt-4 mb-2 text-xs text-neutral-500">
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-blue-500" /> Período actual
          </span>
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-full bg-neutral-600" /> Período anterior
          </span>
        </div>

        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <defs>
                <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
              <XAxis dataKey="name" stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#525252" fontSize={11} tickLine={false} axisLine={false}
                tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`} />
              <Tooltip content={<DarkTooltip formatter={(v: unknown) => formatCop(Number(v))} />} />
              <Area type="monotone" dataKey="previous" stroke="#525252" strokeWidth={1.5}
                strokeDasharray="3 3" fill="transparent" />
              <Area type="monotone" dataKey="actual" stroke="#3b82f6" strokeWidth={2}
                fill="url(#actualGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

/* ============================================================
 * Productos Populares — lista con barras
 * ============================================================ */

function PopularProductsCard({ products, loading }: { products: Array<{ id: string; name: string; sales: number }>; loading: boolean }) {
  const max = Math.max(1, ...products.map((i) => i.sales))
  const colors = ['bg-blue-500', 'bg-violet-500', 'bg-purple-500', 'bg-emerald-500', 'bg-orange-500']

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-1.5">
            Productos Populares
            <Info className="size-3.5 text-neutral-500" />
          </h3>
          <button className="text-xs text-neutral-400 hover:text-white">Ver Más</button>
        </div>
        {loading ? (
          <p className="text-sm text-neutral-500 py-8 text-center">Cargando...</p>
        ) : products.length === 0 ? (
          <p className="text-sm text-neutral-500 py-8 text-center">Sin ventas en este período.</p>
        ) : (
          <div className="space-y-4">
            {products.map((it, idx) => (
              <div key={it.id}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-medium uppercase tracking-wide truncate">{it.name}</span>
                  <span className="text-neutral-400 shrink-0 ml-2">{it.sales} {it.sales === 1 ? 'Venta' : 'Ventas'}</span>
                </div>
                <div className="h-1.5 bg-neutral-900 rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full', colors[idx % colors.length])}
                    style={{ width: `${(it.sales / max) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/* ============================================================
 * Ventas (Pagadas) — Bar chart
 * ============================================================ */

function OrdersChartCard({ data, total, deltaPct }: { data: Array<{ name: string; actual: number; previous: number }>; total: number; deltaPct: number }) {
  const isUp = deltaPct >= 0
  const Icon = isUp ? TrendingUp : TrendingDown
  const color = deltaPct === 0 ? 'text-neutral-500' : isUp ? 'text-emerald-400' : 'text-red-400'

  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="font-semibold flex items-center gap-1.5 mb-3">
          Ventas (Pagadas)
          <Info className="size-3.5 text-neutral-500" />
        </h3>
        <p className="text-3xl font-bold tabular-nums">{total} <span className="text-base font-medium text-neutral-400">órdenes</span></p>
        <p className={cn('text-xs mt-1 flex items-center gap-1', color)}>
          <Icon className="size-3" /> {deltaPct >= 0 ? '↑' : '↓'}{Math.abs(deltaPct)}% vs período anterior
        </p>

        <div className="flex items-center gap-3 mt-4 mb-2 text-xs text-neutral-500">
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-sm bg-blue-500" /> Período actual
          </span>
          <span className="flex items-center gap-1">
            <span className="size-2 rounded-sm bg-neutral-600" /> Período anterior
          </span>
        </div>

        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
              <XAxis dataKey="name" stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip content={<DarkTooltip />} />
              <Bar dataKey="actual" fill="#3b82f6" radius={[2, 2, 0, 0]} />
              <Bar dataKey="previous" fill="#404040" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

/* ============================================================
 * Sesiones — Bar chart simple
 * ============================================================ */

function SessionsChartCard({ data, total }: { data: Array<{ name: string; value: number }>; total: number }) {
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="font-semibold flex items-center gap-1.5 mb-3">
          Sesiones
          <Info className="size-3.5 text-neutral-500" />
        </h3>
        <p className="text-3xl font-bold tabular-nums">{total.toLocaleString('es-CO')}</p>
        <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
          <TrendingUp className="size-3" /> ↑0% vs período anterior
        </p>
        <p className="text-xs text-neutral-500 mt-1">Últimos 7 días</p>

        <div className="h-56 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
              <XAxis dataKey="name" stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#525252" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip content={<DarkTooltip />} />
              <Bar dataKey="value" fill="#3b82f6" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

/* ============================================================
 * Tooltip dark
 * ============================================================ */

function DarkTooltip({ active, payload, label, formatter }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-neutral-900 border border-neutral-700 rounded-md px-3 py-2 text-xs shadow-lg">
      <p className="text-neutral-400 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} className="font-mono">
          <span style={{ color: p.color }}>●</span> {p.name}: {formatter ? formatter(p.value) : p.value}
        </p>
      ))}
    </div>
  )
}

/* ============================================================
 * Helpers + mock data (reemplazar cuando exista endpoint analytics)
 * ============================================================ */

function formatCop(amount: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}
